import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { usePostProductStoreMutation } from '@/services/queries/product-store';
import { PostProductStoreRequest } from '@/services/request/product-store';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { CurrencyInputField } from '@/components/CurrencyInputField';
import { SelectField } from '@/components/SelectField';
import { useTranslation } from 'react-i18next';
import { Partner } from '@/model';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface ProductStoreCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  partners: Partner[];
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const ProductStoreCreateModal = ({ isOpen, partners, onClose, onDone }: ProductStoreCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const postItemMutation = usePostProductStoreMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      file: undefined,
      price: `0`,
      source_url: '',
      publish_at: '',
      is_draft: false,
      is_scheduled: false,
      has_partner: false,
      partner_uuid: '',
      team_uuid: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const price = String(data.price).replace(',', '').replace('.', '');

      const payload: PostProductStoreRequest = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        image: data.file,
        title: data.title,
        price: parseFloat(price),
        source_url: data.source_url,
        is_draft: !!data.is_draft,
        publish_at: data.is_draft ? '' : data.publish_at || '',
        partner_uuid: data.partner_uuid || '',
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_store.components.product_store_create_modal.product_store_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title:
              (resp && resp.message) ||
              t('pages.application_store.components.product_store_create_modal.product_store_create_error'),
          });
        },
      });
    },
    [postItemMutation, onDone, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_store.components.product_store_create_modal.product_store_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_store.components.product_store_create_modal.product_store_create_product_team')}
              placeholder={t('pages.application_store.components.product_store_create_modal.product_store_create_product_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />

            <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
              <InputGroup display='flex' flexDirection='column'>
                <InputField
                  withoutFloating
                  label={t('pages.application_store.components.product_store_create_modal.product_store_create_product_label')}
                  {...register('title')}
                  helperText={t(
                    'pages.application_store.components.product_store_create_modal.product_store_create_product_helper_text',
                  )}
                  maxLength={100}
                  rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
                />
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <CurrencyInputField
                  withoutFloating
                  label={t('pages.application_store.components.product_store_create_modal.product_store_create_price_label')}
                  {...useDebouncedFieldRegistry(() => register('price'))}
                  rootProps={{ isInvalid: !!errors?.price, errorMessage: errors?.price?.message }}
                />
              </InputGroup>
            </Flex>

            <InputField
              withoutFloating
              label={t('pages.application_store.components.product_store_create_modal.product_store_create_link_label')}
              {...register('source_url')}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.source_url, errorMessage: errors?.source_url?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <ImageUpload
                label={t('pages.application_store.components.product_store_create_modal.product_store_create_photo_label')}
                imageURL={''}
                onFileChange={file => setValue('file', file)}
                helpText={t(
                  'pages.application_store.components.product_store_create_modal.product_store_create_photo_helper_text',
                )}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>

            {formValues.is_scheduled && (
              <InputField
                withoutFloating
                label={t('pages.application_store.components.product_store_create_modal.product_store_create_date_label')}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                {...register('publish_at')}
                rootProps={{ isInvalid: !!errors?.publish_at, errorMessage: errors?.publish_at?.message }}
              />
            )}

            <FormControl display='flex' gap={2} alignItems='center'>
              <Text fontWeight='medium'>
                {t('pages.application_store.components.product_store_create_modal.product_store_create_partner_label')}
              </Text>
              <Switch
                name='has_partner'
                isChecked={formValues.has_partner}
                onChange={event => {
                  setValue('has_partner', event.target.checked);
                }}
              />
            </FormControl>

            {formValues.has_partner && (
              <InputGroup display='flex' flexDirection='column' mb={4}>
                <Text fontWeight='medium'>
                  {t('pages.application_store.components.product_store_create_modal.product_store_create_partner_select_label')}
                </Text>
                <SelectField
                  {...register('partner_uuid')}
                  rootProps={{
                    isInvalid: !!errors?.partner_uuid,
                    errorMessage: errors?.partner_uuid?.message?.toString(),
                    mt: 0,
                  }}
                >
                  <option value=''>
                    {t('pages.application_store.components.product_store_create_modal.product_store_create_partner_select_all')}
                  </option>
                  {partners?.map(item => (
                    <option key={item.uuid} value={item.uuid}>
                      {item.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => {
                setValue('is_draft', true);
                handleConfirm();
              }}
              width={{ base: '100%', md: 'initial' }}
              p={0}
              color='brand.500'
              backgroundColor='transparent'
              textDecoration='underline'
              _hover={{
                backgroundColor: 'transparent',
                color: 'brand.500',
              }}
            >
              <ChakraFeatherIcon name='save' />
              {t('pages.application_store.components.product_store_create_modal.product_store_create_save_draft')}
            </Button>

            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_store.components.product_store_create_modal.product_store_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_store.components.product_store_create_modal.product_store_create_submit')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
